import { useEffect, useRef } from 'react';
import { useOpenLayersContext } from '@ais-ui/react-openlayers';
import { useDateTimeFormat } from '../../conversions/useDateFormat';
import Overlay from 'ol/Overlay';
import Feature from 'ol/Feature';
import type { MapBrowserEvent } from 'ol';

interface PointHoverContent {
  title: string;
  subtitle: string | null;
  description: string | null;
  timestamp: string | null;
}

/** Read hover-tooltip metadata from a feature, or null if it carries none. */
function readHoverContent(feature: Feature): PointHoverContent | null {
  const title = feature.get('hoverTitle') as string | undefined;
  if (!title) return null;
  return {
    title,
    subtitle: (feature.get('hoverSubtitle') as string | undefined) ?? null,
    description:
      (feature.get('hoverDescription') as string | undefined) ?? null,
    timestamp: (feature.get('hoverTimestamp') as string | undefined) ?? null,
  };
}

/**
 * Adds a hover tooltip for point features that carry explanatory metadata.
 * Any feature with a `hoverTitle` property shows a small card on hover with an
 * optional subtitle, description, and formatted timestamp. The timestamp uses
 * the user's chosen date format and timezone (via useDateTimeFormat).
 *
 * S2S events use this to explain what their activation/fired position points
 * mean and when they occurred.
 */
export function usePointHoverTooltip() {
  const { map } = useOpenLayersContext();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  // Keep formatter in a ref so the pointer-move handler always uses the latest
  // (timezone/format) without re-creating the overlay on every settings change.
  const formatRef = useRef(formatDateTimeWithTimeZone);
  formatRef.current = formatDateTimeWithTimeZone;

  useEffect(() => {
    if (!map) return;

    // Tooltip DOM — matches the track hover card style.
    const el = document.createElement('div');
    el.className =
      'pointer-events-none rounded-md bg-popover text-popover-foreground shadow-sm border border-l-2 border-l-primary min-w-[140px] max-w-[260px] px-2.5 py-1.5 space-y-1';
    el.style.display = 'none';
    document.body.appendChild(el);

    const overlay = new Overlay({
      element: el,
      offset: [16, -16],
      positioning: 'bottom-left',
      stopEvent: false,
    });
    map.addOverlay(overlay);

    const handlePointerMove = (evt: MapBrowserEvent<PointerEvent>) => {
      if (evt.dragging) {
        el.style.display = 'none';
        return;
      }

      let content: PointHoverContent | null = null;
      map.forEachFeatureAtPixel(
        evt.pixel,
        (featureLike) => {
          if (!('get' in featureLike)) return false;
          const hover = readHoverContent(featureLike as Feature);
          if (!hover) return false;
          content = hover;
          return true;
        },
        { hitTolerance: 4 },
      );

      if (!content) {
        el.style.display = 'none';
        return;
      }

      const { title, subtitle, description, timestamp } = content;

      el.innerHTML = '';

      const titleEl = document.createElement('div');
      titleEl.className = 'text-sm font-semibold';
      titleEl.textContent = title;
      el.appendChild(titleEl);

      if (subtitle) {
        const subtitleEl = document.createElement('div');
        subtitleEl.className = 'text-xs text-muted-foreground';
        subtitleEl.textContent = subtitle;
        el.appendChild(subtitleEl);
      }

      if (description) {
        const descriptionEl = document.createElement('div');
        descriptionEl.className = 'text-xs text-muted-foreground';
        descriptionEl.textContent = description;
        el.appendChild(descriptionEl);
      }

      if (timestamp) {
        const timeEl = document.createElement('div');
        timeEl.className = 'text-xs text-muted-foreground opacity-70';
        timeEl.textContent = formatRef.current(new Date(timestamp), true);
        el.appendChild(timeEl);
      }

      el.style.display = '';
      overlay.setPosition(evt.coordinate);
    };

    map.on('pointermove', handlePointerMove);

    return () => {
      map.un('pointermove', handlePointerMove);
      map.removeOverlay(overlay);
      el.remove();
    };
  }, [map]);
}
